import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import { useTranslation } from '../../../../i18n/useTranslation';
import * as authService from '../../../services/auth.service';
import * as resetPasswordService from '../../../services/reset-password.service';
import { UserLogin } from '../../../login/models';
import { SetNewPasswordDataDto } from '../../models';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { HttpStatusCode } from 'axios';

interface FormState {
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
  general?: string;
}

const ResetPasswordForm: React.FC = () => {
    const [formData, setFormData] = useState<FormState>({
        password: '',
        confirmPassword: '',
    });
    const { t } = useTranslation('translation');
    const [errors, setErrors] = useState<FormErrors>({});
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const showError = (message: string) => toast.error(message);
    const [requestModel, setRequestModel] = useState<SetNewPasswordDataDto>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const messages = {
        tooShort: t('resetPasswordForm.TOOSHORT'),
        noUpper: t('resetPasswordForm.NOUPPER'),
        noLower: t('resetPasswordForm.NOLOWER'),
        noDigit: t('resetPasswordForm.NODIGIT'),
        noSpecial: t('resetPasswordForm.NOSPECIAL'),
        notMatch: t('resetPasswordForm.NOTMATCH'),
        generalError: t('resetPasswordForm.GENERALERROR'),
        success: t('resetPasswordForm.SUCCESS')
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const email = params.get('email');
        const token = params.get('token');

        if (email && token) {
            sessionStorage.setItem('reset_email', email);
            sessionStorage.setItem('reset_token', token);
        }

        const requestModel: SetNewPasswordDataDto = {
            email: email || sessionStorage.getItem('reset_email') || '',
            token: token || sessionStorage.getItem('reset_token') || '',
            password: '',
            confirmPassword: '',
        };

        setRequestModel(requestModel);
    }, []);

    const validatePassword = (password: string): string | null => {
        const minLength = 6;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasDigit = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) return messages.tooShort;
        if (!hasUpperCase) return messages.noUpper;
        if (!hasLowerCase) return messages.noLower;
        if (!hasDigit) return messages.noDigit;
        if (!hasSpecialChar) return messages.noSpecial;

        return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
        setErrors({});
        setSuccessMessage(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const passwordError = validatePassword(formData.password);
        const confirmPasswordError =
        formData.password !== formData.confirmPassword ? messages.notMatch : null;

        if (passwordError || confirmPasswordError) {
            setErrors({
                password: passwordError || undefined,
                confirmPassword: confirmPasswordError || undefined,
            });
            return;
        }
        await confirmPasswordChange();
    };

    async function confirmPasswordChange() {
        try {
            setIsLoading(true);
            const _requestModel = requestModel as SetNewPasswordDataDto;
            _requestModel.password = formData.password;
            _requestModel.confirmPassword = formData.confirmPassword;
            const response = await resetPasswordService.confirmNewPassword(requestModel as SetNewPasswordDataDto);

            if (response.status === HttpStatusCode.NoContent) {
                const logInData: UserLogin = {
                    loginIdentifier: _requestModel.email as string,
                    password: _requestModel.password,
                };
            setIsLoading(false);
            authService.logIn(logInData, navigate, '/', () => {});
            } else {
                showError(t('commonErrors.UNEXPECTED_ERROR'));
                setIsLoading(false);
            }
        } catch (error) {
            showError(t('resetPasswordForm.LINK_NOT_ACTIVE'));
            setIsLoading(false);
        }
    }

    return (
        <Container maxWidth="sm">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mt={5}
                mb={5}
            >
                <Typography variant="h5" gutterBottom>
                    {t('resetPasswordForm.TITLE')}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} width="100%" noValidate>
                {errors.general && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                    {errors.general}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                    {successMessage}
                    </Alert>
                )}

                <TextField
                    label={t('loginForm.password')}
                    type="password"
                    name="password"
                    fullWidth
                    margin="normal"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    required
                    size='small'
                />

                <TextField
                    label={t('resetPasswordForm.confirmPassword')}
                    type="password"
                    name="confirmPassword"
                    fullWidth
                    margin="normal"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                    required
                    size='small'
                />
                {isLoading && 
                        <LinearProgress 
                            sx={{
                                marginBottom: '0.5rem',
                                backgroundColor: '#b85353',
                                    '& .MuiLinearProgress-bar': {
                                backgroundColor: '#821818',
                                },
                            }}
                        />
                    }
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textTransform: 'none',
                        flexShrink: 0,
                        backgroundColor: '#821818',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '14px',
                        '&: hover': {
                            backgroundColor: '#821818',
                            boxShadow: 'none'
                        },
                        borderRadius: 'unset',
                        mt: 2 
                    }}
                >
                    {t('SEND')}
                </Button>
                </Box>
            </Box>
        </Container>
    );
};

export default ResetPasswordForm;